/**
 * Create Agent Identity page component.
 *
 * @module pages/CreateAgentIdentityPage
 */

import { useState } from 'react';
import { useNavigate, Link } from 'react-router';
import { ArrowLeft, Save, Plus, X } from 'lucide-react';
import { PageHeader } from '@/components/layout';
import { Card, CardHeader, CardBody, CardFooter, Button, Input } from '@/components/ui';
import { useCreateAgentIdentity } from '@/hooks';
import type { AgentIdentityCreateRequest, AgentIdentityResponse } from '@/types';

interface FormState {
  displayName: string;
  tenantId: string;
  serviceTreeId: string;
  sponsors: string[];
}

interface FormErrors {
  displayName?: string;
  tenantId?: string;
  serviceTreeId?: string;
  sponsors?: string;
}

const initialFormState: FormState = {
  displayName: '',
  tenantId: '',
  serviceTreeId: '',
  sponsors: [],
};

// GUID regex pattern
const GUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Create agent identity page
 */
export function CreateAgentIdentityPage() {
  const navigate = useNavigate();
  const createMutation = useCreateAgentIdentity();
  const [form, setForm] = useState<FormState>(initialFormState);
  const [errors, setErrors] = useState<FormErrors>({});
  const [showResults, setShowResults] = useState(false);
  const [createdIdentity, setCreatedIdentity] = useState<AgentIdentityResponse | null>(null);
  const [timeTakenMs, setTimeTakenMs] = useState<number>(0);
  const [newSponsor, setNewSponsor] = useState('');

  const handleChange = (field: keyof FormState, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleAddSponsor = () => {
    const trimmed = newSponsor.trim();
    if (trimmed && !form.sponsors.includes(trimmed)) {
      setForm((prev) => ({ ...prev, sponsors: [...prev.sponsors, trimmed] }));
      setNewSponsor('');
      if (errors.sponsors) {
        setErrors((prev) => ({ ...prev, sponsors: undefined }));
      }
    }
  };

  const handleRemoveSponsor = (index: number) => {
    setForm((prev) => ({
      ...prev,
      sponsors: prev.sponsors.filter((_, i) => i !== index),
    }));
  };

  const handleSponsorKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddSponsor();
    }
  };

  const validate = (): boolean => {
    const newErrors: FormErrors = {};

    if (!form.displayName.trim()) {
      newErrors.displayName = 'Display name is required';
    } else if (form.displayName.length > 256) {
      newErrors.displayName = 'Display name must be 256 characters or less';
    }

    if (!form.tenantId.trim()) {
      newErrors.tenantId = 'Tenant ID is required';
    } else if (!GUID_PATTERN.test(form.tenantId.trim())) {
      newErrors.tenantId = 'Tenant ID must be a valid GUID';
    }

    if (!form.serviceTreeId.trim()) {
      newErrors.serviceTreeId = 'Service Tree ID is required';
    } else if (!GUID_PATTERN.test(form.serviceTreeId.trim())) {
      newErrors.serviceTreeId = 'Service Tree ID must be a valid GUID';
    }

    if (form.sponsors.length === 0) {
      newErrors.sponsors = 'At least one sponsor is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    const startTime = performance.now();

    const request: AgentIdentityCreateRequest = {
      displayName: form.displayName.trim(),
      tenantId: form.tenantId.trim(),
      serviceTreeId: form.serviceTreeId.trim(),
      sponsors: form.sponsors,
    };

    createMutation.mutate(request, {
      onSuccess: (data) => {
        const endTime = performance.now();
        setTimeTakenMs(Math.round(endTime - startTime));
        setCreatedIdentity(data);
        setShowResults(true);
      },
    });
  };

  const handleViewIdentity = () => {
    navigate('/agent-identities');
  };

  const handleCreateAnother = () => {
    setShowResults(false);
    setCreatedIdentity(null);
    setTimeTakenMs(0);
    setForm(initialFormState);
  };

  // If showing results, display the results page
  if (showResults && createdIdentity) {
    return (
      <div>
        <PageHeader
          title="Agent Identity Created"
          description="Your agent identity has been created successfully"
          breadcrumbs={[
            { label: 'Dashboard', href: '/dashboard' },
            { label: 'Agent Identities', href: '/agent-identities' },
            { label: 'Create' },
          ]}
        />

        <div className="space-y-6">
          <Card>
            <CardHeader title="Creation Summary" description="Agent identity creation details" />
            <CardBody>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                  <div className="text-sm text-green-700 dark:text-green-400">Status</div>
                  <div className="text-2xl font-bold text-green-700 dark:text-green-300">
                    Success
                  </div>
                </div>
                <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <div className="text-sm text-blue-700 dark:text-blue-400">Time Taken</div>
                  <div className="text-2xl font-bold text-blue-700 dark:text-blue-300">
                    {timeTakenMs.toLocaleString()} ms
                  </div>
                </div>
              </div>
            </CardBody>
          </Card>

          <Card>
            <CardHeader
              title="Agent Identity Details"
              description="Details of the created agent identity"
            />
            <CardBody>
              <div className="space-y-3">
                <div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">ID</div>
                  <div className="font-medium text-gray-900 dark:text-gray-100 font-mono">
                    {createdIdentity.id}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">Display Name</div>
                  <div className="font-medium text-gray-900 dark:text-gray-100">
                    {createdIdentity.displayName}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">Tenant ID</div>
                  <div className="font-medium text-gray-900 dark:text-gray-100 font-mono">
                    {createdIdentity.tenantId}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">Service Tree ID</div>
                  <div className="font-medium text-gray-900 dark:text-gray-100 font-mono">
                    {createdIdentity.serviceTreeId}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">Blueprint</div>
                  <div className="font-medium text-gray-900 dark:text-gray-100">
                    {createdIdentity.blueprint.type}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">Sponsors</div>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {createdIdentity.sponsors.map((sponsor, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 font-mono"
                      >
                        {sponsor.length > 50 ? `${sponsor.substring(0, 50)}...` : sponsor}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </CardBody>
          </Card>

          <Card>
            <CardFooter>
              <div className="flex justify-end gap-3">
                <Button variant="secondary" onClick={handleCreateAnother}>
                  Create Another
                </Button>
                <Button onClick={handleViewIdentity}>View Agent Identities</Button>
              </div>
            </CardFooter>
          </Card>
        </div>
      </div>
    );
  }

  // Normal form view
  return (
    <div>
      <PageHeader
        title="Create Agent Identity"
        description="Create a new agent identity for AI agents to authenticate to Azure resources"
        breadcrumbs={[
          { label: 'Dashboard', href: '/dashboard' },
          { label: 'Agent Identities', href: '/agent-identities' },
          { label: 'Create' },
        ]}
        action={
          <Link to="/agent-identities">
            <Button variant="secondary" leftIcon={<ArrowLeft className="h-4 w-4" />}>
              Cancel
            </Button>
          </Link>
        }
      />

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Basic Information */}
          <Card>
            <CardHeader
              title="Basic Information"
              description="Configure the agent identity details"
            />
            <CardBody className="space-y-4">
              <Input
                label="Display Name"
                placeholder="My AI Agent"
                value={form.displayName}
                onChange={(e) => handleChange('displayName', e.target.value)}
                error={errors.displayName}
                helperText="A human-readable name for the agent identity (max 256 characters)"
                required
              />
              <Input
                label="Tenant ID"
                placeholder="00000000-0000-0000-0000-000000000000"
                value={form.tenantId}
                onChange={(e) => handleChange('tenantId', e.target.value)}
                error={errors.tenantId}
                helperText="The Azure AD tenant ID where the agent identity will be created"
                required
              />
              <Input
                label="Service Tree ID"
                placeholder="00000000-0000-0000-0000-000000000000"
                value={form.serviceTreeId}
                onChange={(e) => handleChange('serviceTreeId', e.target.value)}
                error={errors.serviceTreeId}
                helperText="The Service Tree ID for tracking ownership (Microsoft 1P requirement)"
                required
              />
            </CardBody>
          </Card>

          {/* Sponsors */}
          <Card>
            <CardHeader
              title="Sponsors"
              description="Add sponsors who are accountable for this agent identity"
            />
            <CardBody className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  Add Sponsor <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newSponsor}
                    onChange={(e) => setNewSponsor(e.target.value)}
                    onKeyDown={handleSponsorKeyDown}
                    placeholder="https://graph.microsoft.com/v1.0/users/{user-id}"
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-[#161616] text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                  />
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={handleAddSponsor}
                    disabled={!newSponsor.trim()}
                    leftIcon={<Plus className="h-4 w-4" />}
                  >
                    Add
                  </Button>
                </div>
                <p className="mt-1.5 text-sm text-gray-500 dark:text-gray-400">
                  Enter Microsoft Graph URLs for users or groups who will sponsor this agent
                </p>
                {errors.sponsors && (
                  <p className="mt-1.5 text-sm text-red-500">{errors.sponsors}</p>
                )}
              </div>

              {form.sponsors.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Current Sponsors ({form.sponsors.length})
                  </label>
                  <div className="space-y-2">
                    {form.sponsors.map((sponsor, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-2 bg-gray-50 dark:bg-[#161616] border border-gray-200 dark:border-[#2a2a2a] rounded-lg"
                      >
                        <span className="text-sm text-gray-700 dark:text-gray-300 font-mono truncate flex-1 mr-2">
                          {sponsor}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleRemoveSponsor(index)}
                          className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                          aria-label={`Remove sponsor ${index + 1}`}
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardBody>
            <CardFooter>
              <div className="flex justify-end gap-3">
                <Link to="/agent-identities">
                  <Button variant="secondary" type="button">
                    Cancel
                  </Button>
                </Link>
                <Button
                  type="submit"
                  leftIcon={<Save className="h-4 w-4" />}
                  isLoading={createMutation.isPending}
                >
                  Create Agent Identity
                </Button>
              </div>
            </CardFooter>
          </Card>
        </div>
      </form>
    </div>
  );
}
